import React, { useState, useEffect } from 'react';
import Parse from 'parse';
import { BethelAppClasses } from '../../api/config';

const GivingDashboard = () => {
    const [payments, setPayments] = useState([]);
    const [givingTypes, setGivingTypes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [showAddTypeForm, setShowAddTypeForm] = useState(false);
    const [dateRange, setDateRange] = useState({
        startDate: new Date(new Date().setDate(new Date().getDate() - 7)).toISOString().split('T')[0],
        endDate: new Date().toISOString().split('T')[0]
    });
    const [summary, setSummary] = useState({
        total: 0,
        byType: {},
        count: 0
    });

    useEffect(() => {
        fetchGivingData();
    }, [dateRange]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (successMessage) {
            const timer = setTimeout(() => {
                setSuccessMessage('');
            }, 5000);
            return () => clearTimeout(timer);
        }
    }, [successMessage]);

    const fetchGivingData = async () => {
        try {
            setLoading(true);
            setError('');

            // Save current Parse configuration
            const originalServerURL = Parse.serverURL;
            const originalAppId = Parse.applicationId;
            
            // Switch to Bethel app server
            Parse.serverURL = 'https://app.bethelchristiancenter.com/parse';
            Parse._initialize('bethel', null, null);

            // Fetch active giving types
            const typesData = await Parse.Cloud.run('getActiveGivingTypes');
            setGivingTypes(typesData || []);

            // Fetch payments for date range
            const Payment = Parse.Object.extend(BethelAppClasses.Payment);
            const query = new Parse.Query(Payment);
            
            query.equalTo('status', 'completed');
            query.greaterThanOrEqualTo('createdAt', new Date(dateRange.startDate));
            query.lessThanOrEqualTo('createdAt', new Date(dateRange.endDate + 'T23:59:59'));
            query.include('givingType');
            query.include('user');
            query.descending('createdAt');
            query.limit(1000);

            const paymentsData = await query.find();
            setPayments(paymentsData);

            // Calculate summary
            calculateSummary(paymentsData, typesData);

            // Restore original Parse configuration
            Parse.serverURL = originalServerURL;
            Parse._initialize(originalAppId, null, null);
        } catch (err) {
            console.error('Error fetching giving data:', err);
            setError('Failed to load giving data. Please try again.');
            
            // Restore original Parse configuration on error
            const originalServerURL = 'https://forms.bethelchristiancenter.com/api/parse';
            const originalAppId = 'myAppId';
            Parse.serverURL = originalServerURL;
            Parse._initialize(originalAppId, null, null);
        } finally {
            setLoading(false);
        }
    };

    const calculateSummary = (paymentsData, typesData) => {
        let total = 0;
        const byType = {};

        // Initialize all giving types with 0
        typesData.forEach(type => {
            byType[type.get('givingTypeName')] = {
                amount: 0,
                count: 0,
                typeId: type.id
            };
        });

        // Calculate totals
        paymentsData.forEach(payment => {
            const amount = payment.get('amount') || 0;
            total += amount;

            const typeName = payment.get('givingType');
            if (byType[typeName]) {
                byType[typeName].amount += amount;
                byType[typeName].count += 1;
            }
        });

        setSummary({
            total,
            byType,
            count: paymentsData.length
        });
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD'
        }).format(amount); // Assuming amounts are stored in cents
    };

    const formatDate = (date) => {
        return new Date(date).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const createGivingType = async (e) => {
        e.preventDefault();
        setError('');
        setSuccessMessage('');
        
        const typeName = e.target.typeName.value.trim();
        const description = e.target.description.value.trim();
        
        if (!typeName) {
            setError('Giving type name is required.');
            return;
        }
        
        try {
            // Save current Parse configuration
            const originalServerURL = Parse.serverURL;
            const originalAppId = Parse.applicationId;
            
            // Switch to Bethel app server
            Parse.serverURL = 'https://app.bethelchristiancenter.com/parse';
            Parse._initialize('bethel', null, null);

            // Create new giving type
            const GivingType = Parse.Object.extend(BethelAppClasses.GivingType);
            const givingType = new GivingType();
            
            givingType.set('givingTypeName', typeName);
            givingType.set('givingTypeDescription', description);
            givingType.set('active', true);
            givingType.set('order', givingTypes.length); // Set order as last

            await givingType.save();
            
            // Restore original Parse configuration
            Parse.serverURL = originalServerURL;
            Parse._initialize(originalAppId, null, null);
            
            setSuccessMessage(`Giving type "${typeName}" created successfully!`);
            setShowAddTypeForm(false);
            e.target.reset();
            
            // Refresh data
            fetchGivingData();
        } catch (err) {
            console.error('Error creating giving type:', err);
            setError('Failed to create giving type. Please try again.');
            
            // Restore original Parse configuration on error
            const originalServerURL = 'https://forms.bethelchristiancenter.com/api/parse';
            const originalAppId = 'myAppId';
            Parse.serverURL = originalServerURL;
            Parse._initialize(originalAppId, null, null);
        }
    };

    const toggleGivingTypeStatus = async (givingTypeId, currentStatus) => {
        try {
            // Save current Parse configuration
            const originalServerURL = Parse.serverURL;
            const originalAppId = Parse.applicationId;
            
            // Switch to Bethel app server
            Parse.serverURL = 'https://app.bethelchristiancenter.com/parse';
            Parse._initialize('bethel', null, null);

            const GivingType = Parse.Object.extend(BethelAppClasses.GivingType);
            const query = new Parse.Query(GivingType);
            const givingType = await query.get(givingTypeId);
            
            givingType.set('active', !currentStatus);
            await givingType.save();
            
            // Restore original Parse configuration
            Parse.serverURL = originalServerURL;
            Parse._initialize(originalAppId, null, null);
            
            setSuccessMessage(`Giving type ${!currentStatus ? 'activated' : 'deactivated'} successfully!`);
            
            // Refresh data
            fetchGivingData();
        } catch (err) {
            console.error('Error updating giving type:', err);
            setError('Failed to update giving type. Please try again.');
            
            // Restore original Parse configuration on error
            const originalServerURL = 'https://forms.bethelchristiancenter.com/api/parse';
            const originalAppId = 'myAppId';
            Parse.serverURL = originalServerURL;
            Parse._initialize(originalAppId, null, null);
        }
    };

    const exportToCSV = () => {
        const headers = ['Date', 'Donor', 'Type', 'Amount', 'Status'];
        const rows = payments.map(payment => {
            const user = payment.get('user');
            const donorName = user 
                ? `${user.get('firstName')} ${user.get('lastName')}`.trim()
                : payment.get('guestFirstName') + ' ' + payment.get('guestLastName');
            
            return [
                formatDate(payment.get('createdAt')),
                donorName || 'Anonymous',
                payment.get('givingType') || 'Unknown',
                formatCurrency(payment.get('amount')),
                payment.get('status')
            ];
        });

        const csvContent = [
            headers.join(','),
            ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
        ].join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `giving-report-${dateRange.startDate}-to-${dateRange.endDate}.csv`;
        a.click();
        window.URL.revokeObjectURL(url);
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    return (
        <div>
            <h2 className="text-2xl font-bold mb-6">Weekly Giving Dashboard</h2>

            {error && (
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
                    {error}
                </div>
            )}

            {successMessage && (
                <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4">
                    {successMessage}
                </div>
            )}

            {/* Date Range Filter */}
            <div className="bg-gray-50 rounded-lg p-4 mb-6">
                <div className="flex flex-wrap items-end gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Start Date
                        </label>
                        <input
                            type="date"
                            value={dateRange.startDate}
                            onChange={(e) => setDateRange(prev => ({ ...prev, startDate: e.target.value }))}
                            className="px-3 py-2 border rounded-lg focus:outline-none focus:border-blue-500"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            End Date
                        </label>
                        <input
                            type="date"
                            value={dateRange.endDate}
                            onChange={(e) => setDateRange(prev => ({ ...prev, endDate: e.target.value }))}
                            className="px-3 py-2 border rounded-lg focus:outline-none focus:border-blue-500"
                        />
                    </div>
                    <button
                        onClick={fetchGivingData}
                        className="bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors"
                    >
                        Update
                    </button>
                    <button
                        onClick={exportToCSV}
                        className="bg-green-600 text-white py-2 px-4 rounded-lg hover:bg-green-700 transition-colors"
                        disabled={payments.length === 0}
                    >
                        Export CSV
                    </button>
                </div>
            </div>

            {/* Giving Types Management */}
            <div className="bg-white rounded-lg shadow mb-6">
                <div className="px-6 py-4 border-b flex justify-between items-center">
                    <h3 className="text-lg font-semibold">Giving Types Management</h3>
                    <button
                        onClick={() => setShowAddTypeForm(!showAddTypeForm)}
                        className="bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors"
                    >
                        {showAddTypeForm ? 'Cancel' : 'Add New Type'}
                    </button>
                </div>
                <div className="p-6">
                    {showAddTypeForm && (
                        <div className="bg-gray-50 rounded-lg p-4 mb-6">
                            <h4 className="font-medium mb-4">Create New Giving Type</h4>
                            <form onSubmit={createGivingType} className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Type Name *
                                    </label>
                                    <input
                                        type="text"
                                        name="typeName"
                                        required
                                        className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:border-blue-500"
                                        placeholder="e.g., Missions, Building Fund, Special Offering"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Description (Optional)
                                    </label>
                                    <textarea
                                        name="description"
                                        rows={3}
                                        className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:border-blue-500"
                                        placeholder="Brief description of this giving type..."
                                    />
                                </div>
                                <div className="flex gap-3">
                                    <button
                                        type="submit"
                                        className="bg-green-600 text-white py-2 px-4 rounded-lg hover:bg-green-700 transition-colors"
                                    >
                                        Create Type
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setShowAddTypeForm(false)}
                                        className="bg-gray-500 text-white py-2 px-4 rounded-lg hover:bg-gray-600 transition-colors"
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </form>
                        </div>
                    )}

                    {/* Existing Giving Types */}
                    <div>
                        <h4 className="font-medium mb-3">Existing Giving Types</h4>
                        <div className="space-y-2">
                            {givingTypes.map((type) => (
                                <div
                                    key={type.id}
                                    className={`flex items-center justify-between p-3 rounded border ${
                                        type.get('active') ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'
                                    }`}
                                >
                                    <div className="flex-1">
                                        <h5 className="font-medium">{type.get('givingTypeName')}</h5>
                                        {type.get('givingTypeDescription') && (
                                            <p className="text-sm text-gray-600 mt-1">
                                                {type.get('givingTypeDescription')}
                                            </p>
                                        )}
                                    </div>
                                    <div className="flex items-center gap-3">
                                        <span
                                            className={`px-2 py-1 text-xs font-medium rounded ${
                                                type.get('active')
                                                    ? 'bg-green-100 text-green-700'
                                                    : 'bg-gray-100 text-gray-700'
                                            }`}
                                        >
                                            {type.get('active') ? 'Active' : 'Inactive'}
                                        </span>
                                        <button
                                            onClick={() => toggleGivingTypeStatus(type.id, type.get('active'))}
                                            className={`text-sm font-medium ${
                                                type.get('active')
                                                    ? 'text-red-600 hover:text-red-800'
                                                    : 'text-green-600 hover:text-green-800'
                                            }`}
                                        >
                                            {type.get('active') ? 'Deactivate' : 'Activate'}
                                        </button>
                                    </div>
                                </div>
                            ))}
                            {givingTypes.length === 0 && (
                                <p className="text-gray-500 text-center py-4">
                                    No giving types found. Create your first giving type above.
                                </p>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div className="bg-white rounded-lg shadow p-6">
                    <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider">
                        Total Giving
                    </h3>
                    <p className="text-3xl font-bold text-gray-900 mt-2">
                        {formatCurrency(summary.total)}
                    </p>
                </div>
                <div className="bg-white rounded-lg shadow p-6">
                    <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider">
                        Total Transactions
                    </h3>
                    <p className="text-3xl font-bold text-gray-900 mt-2">
                        {summary.count}
                    </p>
                </div>
                <div className="bg-white rounded-lg shadow p-6">
                    <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider">
                        Average Gift
                    </h3>
                    <p className="text-3xl font-bold text-gray-900 mt-2">
                        {summary.count > 0 ? formatCurrency(summary.total / summary.count) : '$0.00'}
                    </p>
                </div>
            </div>

            {/* Giving by Type */}
            <div className="bg-white rounded-lg shadow mb-6">
                <div className="px-6 py-4 border-b">
                    <h3 className="text-lg font-semibold">Giving by Type</h3>
                </div>
                <div className="p-6">
                    <div className="space-y-4">
                        {Object.entries(summary.byType).map(([typeName, data]) => (
                            <div key={data.typeId} className="flex items-center justify-between">
                                <div className="flex-1">
                                    <div className="flex items-center justify-between mb-1">
                                        <span className="font-medium">{typeName}</span>
                                        <span className="text-sm text-gray-500">
                                            {data.count} transactions
                                        </span>
                                    </div>
                                    <div className="w-full bg-gray-200 rounded-full h-2">
                                        <div
                                            className="bg-blue-600 h-2 rounded-full"
                                            style={{
                                                width: `${summary.total > 0 ? (data.amount / summary.total * 100) : 0}%`
                                            }}
                                        ></div>
                                    </div>
                                </div>
                                <div className="ml-4 text-right">
                                    <p className="font-semibold">{formatCurrency(data.amount)}</p>
                                    <p className="text-sm text-gray-500">
                                        {summary.total > 0 ? Math.round(data.amount / summary.total * 100) : 0}%
                                    </p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Recent Transactions */}
            <div className="bg-white rounded-lg shadow">
                <div className="px-6 py-4 border-b">
                    <h3 className="text-lg font-semibold">Recent Transactions</h3>
                </div>
                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Date
                                </th>
                                {/*<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">*/}
                                {/*    Donor*/}
                                {/*</th>*/}
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Type
                                </th>
                                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Amount
                                </th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                            {payments.slice(0, 10).map((payment) => {
                                // const user = payment.get('user');
                                // const donorName = user
                                //     ? `${user.get('firstName')} ${user.get('lastName')}`.trim()
                                //     : payment.get('guestFirstName') + ' ' + payment.get('guestLastName');

                                return (
                                    <tr key={payment.id}>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                            {formatDate(payment.get('createdAt'))}
                                        </td>
                                        {/*<td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">*/}
                                        {/*    {donorName || 'Anonymous'}*/}
                                        {/*</td>*/}
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                            {payment.get('givingType') || 'Unknown'}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                                            {formatCurrency(payment.get('amount'))}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                    {payments.length === 0 && (
                        <p className="text-center py-8 text-gray-500">
                            No transactions found for this date range.
                        </p>
                    )}
                </div>
            </div>
        </div>
    );
};

export default GivingDashboard;